import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import { Visibility, VisibilityOff, LockOutlined, EmailOutlined, SecurityOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiList, invokeApi } from "../services/apiServices";
import Logo from "../assets/ar-infotech-solutions.png";
import { useUser } from "../context/UserContext";

const PURPLE_THEME = "#1A237E";
const ACCENT_COLOR = "#7B6FE8";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const { login } = useUser();

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      showSnackbar("Please fill in email and password", "warning");
      return;
    }

    setLoading(true);
    const params = { email: email.trim(), password: password.trim() };

    try {
      const response = await invokeApi(apiList.login, params);

      if (response?.status === 200 && response.data?.responseCode === "200") {
        let assignedRoles = response.data.roles;
        if (!assignedRoles || assignedRoles.length === 0) {
          assignedRoles = email.toLowerCase().includes("sales") ? ["Admin"] : ["Super Admin"];
        }
        try {
          localStorage.removeItem('ar_dashboard_logged_out');
        } catch (e) {}
        login({
          name: response.data.name || "Administrator",
          email: email.trim(),
          id: response.data.userId,
          roles: assignedRoles
        }, response.data.token);
        showSnackbar("Login successful! Redirecting to Dashboard...", "success");
        setTimeout(() => navigate('/dashboard'), 800);
      } else {
        showSnackbar(response?.data?.responseMessage || "Invalid email or password", "error");
      }
    } catch (error) {
      showSnackbar("Connection error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", bgcolor: "#F4F5FB" }}>
      <Grid container sx={{ flex: 1 }}>
        {/* Left: AR Infotech Solutions Admin Login Form */}
        <Grid
          item
          xs={12}
          md={5}
          lg={4}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            px: { xs: 3, sm: 6 },
            py: 5,
            bgcolor: "#FFFFFF",
            boxShadow: "4px 0 20px rgba(0,0,0,0.05)",
            zIndex: 2,
          }}
        >
          {/* Header Brand */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
              <img
                src={Logo}
                alt="AR INFOTECH SOLUTIONS"
                style={{ height: "48px", objectFit: "contain" }}
              />
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 800, color: PURPLE_THEME, mb: 0.5, letterSpacing: -0.5 }}>
              Admin Portal
            </Typography>
            <Typography variant="body2" sx={{ color: "#666677", mb: 4 }}>
              Sign in with your admin credentials to access control panel.
            </Typography>

            <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", p: 3, borderRadius: 2, bgcolor: "#FAFAFE" }}>
              <form onSubmit={handleLogin}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#333" }}>
                  Super Admin / Admin Login
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  label="Admin Email Address"
                  placeholder="arinfotechsolutions38@gmail.com"
                  variant="outlined"
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ color: ACCENT_COLOR, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  variant="outlined"
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: ACCENT_COLOR, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePasswordVisibility} edge="end" size="small">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={<SecurityOutlined />}
                  sx={{
                    mt: 3,
                    py: 1.2,
                    bgcolor: PURPLE_THEME,
                    borderRadius: 1.5,
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#0D1359" },
                  }}
                >
                  {loading ? "AUTHENTICATING..." : "SIGN IN TO DASHBOARD"}
                </Button>
              </form>
            </Paper>
          </Box>

          {/* Footer Copyright */}
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: "#888899" }}>
              © 2026 AR INFOTECH SOLUTIONS. All rights reserved.
            </Typography>
          </Box>
        </Grid>

        {/* Right: Modern Dashboard Hero Display */}
        <Grid
          item
          xs={false}
          md={7}
          lg={8}
          sx={{
            background: "linear-gradient(135deg, #1A237E 0%, #303F9F 50%, #7B6FE8 100%)",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 6,
            color: "#FFFFFF",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative Circles */}
          <Box
            sx={{
              position: "absolute",
              width: "500px",
              height: "500px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.04)",
              top: "-100px",
              right: "-100px",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: "300px",
              height: "300px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
              bottom: "-50px",
              left: "-50px",
            }}
          />

          <Box sx={{ maxWidth: 520, textAlign: "center", zIndex: 1 }}>
            <Typography variant="overline" sx={{ letterSpacing: 2, color: "#9484E8", fontWeight: 700 }}>
              ENTERPRISE DASHBOARD CONTROL
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, mt: 1, lineHeight: 1.2 }}>
              AR Infotech Solutions
            </Typography>
            <Typography variant="body1" sx={{ color: "#E0E0FF", lineHeight: 1.6, mb: 4 }}>
              Complete administrative control for inventory, software licenses, categories, orders, customers, and invoice management.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity} sx={{ width: "100%", borderRadius: 2 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;